/**
 * Every payment in the system, both directions, fees included — the
 * portfolio-wide money-movement ledger. Incoming rows are tenant rent
 * payments (`payments`); outgoing rows are owner disbursements
 * (`owner_disbursements`), with their fee lines (FEE_PAYMENT/
 * FEE_MANAGEMENT/FEE_ACCOUNT_MONTHLY) summed into one Fee column. See
 * `modules/reports/service.ts`'s `paymentsHistory` for why this reads the
 * real disbursement tables rather than the stale `disbursementApprovedAt`
 * flag the older owner/tenant "master report" timeline still uses.
 *
 * A standalone Payment Center page, like Rent Roll and Delinquency — not a
 * Reports-tab — but gated `reports:read` rather than `ledger:read` (its
 * Payment Center siblings' capability): this report reaches across every
 * tenant and every owner by an arbitrary caller-supplied id, which is
 * exactly the "financial reports are not for MAINTENANCE" line CLAUDE.md
 * already draws for the Reports module, not the "everyone" posture
 * `ledger:read` grants portal actors too.
 */

import { useEffect, useState } from 'react';
import { api } from '../../lib/api.js';
import { DataTable, type Column } from '../../components/DataTable.js';
import { ReportFilterBar } from '../../components/ReportFilterBar.js';
import { humanizeEnum } from '../../components/Form.js';

interface PaymentHistoryRow {
  id: string;
  eventDate: string;
  direction: 'IN' | 'OUT';
  partyName: string;
  propertyName: string | null;
  method: string | null;
  status: string;
  amount: string;
  feeAmount: string;
  description: string;
}

interface PaymentsHistoryResponse {
  data: PaymentHistoryRow[];
  total: number;
}

const columns: Column<PaymentHistoryRow>[] = [
  { key: 'date', header: 'Date', render: (r) => r.eventDate.slice(0, 10) },
  {
    key: 'direction',
    header: 'Direction',
    render: (r) => (r.direction === 'IN' ? 'Incoming' : 'Outgoing'),
  },
  { key: 'party', header: 'Tenant / Owner', render: (r) => r.partyName },
  { key: 'property', header: 'Property', render: (r) => r.propertyName ?? '—' },
  { key: 'method', header: 'Method', render: (r) => (r.method ? humanizeEnum(r.method) : '—') },
  { key: 'status', header: 'Status', render: (r) => humanizeEnum(r.status) },
  { key: 'amount', header: 'Amount', numeric: true, render: (r) => `$${r.amount}` },
  { key: 'fee', header: 'Fee', numeric: true, render: (r) => `$${r.feeAmount}` },
  { key: 'description', header: 'Description', render: (r) => r.description },
];

export function PaymentsHistory() {
  const [ownerId, setOwnerId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [result, setResult] = useState<PaymentsHistoryResponse | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const query = new URLSearchParams();
    if (ownerId) query.set('ownerId', ownerId);
    if (tenantId) query.set('tenantId', tenantId);
    api<PaymentsHistoryResponse>(`/reports/payments-history?${query.toString()}`)
      .then((response) => {
        if (!cancelled) setResult(response);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ownerId, tenantId]);

  return (
    <section className="stack">
      <h1>Payments history</h1>
      <p className="muted">
        Every payment in the system — tenant rent payments coming in, owner disbursements going out
        — with each row's fee broken out. Narrow to one tenant or one owner below, or leave both
        unset for the whole portfolio.
      </p>

      <ReportFilterBar
        ownerId={ownerId}
        onOwnerChange={setOwnerId}
        tenantId={tenantId}
        onTenantChange={setTenantId}
        exportPath="/reports/payments-history"
        exportParams={{
          ownerId: ownerId || undefined,
          tenantId: tenantId || undefined,
        }}
      />

      <DataTable
        columns={columns}
        rows={result?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ?? undefined}
        emptyMessage="No payments match this filter."
      />
    </section>
  );
}
